import React, { useEffect, useState } from 'react';
import { Box, MenuItem, TextField, Typography } from '@mui/material';
import { TaskService } from '../../services/task.service';
import { ITask, TaskStatus } from '../../interfaces/task.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { humanizeEnum } from '../../utils/format';
import TaskListTable from './TaskListTable';

const PAGE_SIZE = 10;
// F8: status filter (incl. Blocked) and the "My deadlines" view
const FILTER_STATUSES: TaskStatus[] = ['OPEN', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'CANCELLED'];

const MyTasks: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [tasks, setTasks] = useState<ITask[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState<TaskStatus | ''>('');
    const [view, setView] = useState<'all' | 'deadlines'>('all');

    const fetchList = async (p: number) => {
        setLoading(true);
        try {
            const data = await TaskService.myTasks(institutionId, p, PAGE_SIZE, status ? [status] : undefined,
                view === 'deadlines' ? 'deadlines' : undefined);
            setTasks(data.content);
            setTotalPages(data.totalPages);
        } catch (err) {
            toast.showApiError(err, 'Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, status, view]);

    const handleStatusChange = async (t: ITask, next: TaskStatus, blockedReason?: string) => {
        try {
            await TaskService.updateStatus(t.id, next, blockedReason);
            toast.showSuccess('Status updated');
            fetchList(page);
        } catch (err) {
            toast.showApiError(err, 'Failed to update status');
        }
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
            <Typography variant="h5" sx={{ mb: 2 }}>My Tasks</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField data-help="tasks.my.field.view" select size="small" label="View" value={view} sx={{ flex: '1 1 200px', maxWidth: 260 }}
                    onChange={e => { setView(e.target.value as 'all' | 'deadlines'); setPage(0); }}>
                    <MenuItem value="all">All my tasks</MenuItem>
                    <MenuItem value="deadlines">My deadlines (open, due soonest)</MenuItem>
                </TextField>
                <TextField data-help="tasks.my.field.status" select size="small" label="Status" value={status} sx={{ flex: '1 1 180px', maxWidth: 240 }}
                    onChange={e => { setStatus(e.target.value as TaskStatus | ''); setPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    {FILTER_STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
                </TextField>
            </Box>
            <TaskListTable
                tasks={tasks}
                loading={loading}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                mode="assignee"
                onStatusChange={handleStatusChange}
            />
        </Box>
    );
};

export default MyTasks;
